import { useMemo } from "react";
import Marquee from "react-fast-marquee";
import moment from "moment";
import { useActiveMatchQuery } from "../../../store/service/ActiveMatcheService";
import { useDashboardQuery } from "../../../store/service/userlistService";
import { LobbySportIcon } from "../../pages/client/lobbySportIcons";

const fmt = (value) =>
  Number(value ?? 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const STAT_CARDS = [
  { key: "balance", label: "Balance", tone: "balance" },
  { key: "exposure", label: "Exposure", tone: "exposure" },
  { key: "creditPts", label: "Credit pts", tone: "credit" },
  { key: "allPts", label: "All pts", tone: "all" },
  { key: "settlementPts", label: "Settlement pts", tone: "settlement" },
  { key: "upperPts", label: "Upper pts", tone: "upper" },
  { key: "downPts", label: "Down pts", tone: "down" },
];

const CHART_ITEMS = [
  { key: "creditPts", label: "Credit pts", color: "#7c3aed" },
  { key: "allPts", label: "All pts", color: "#f59e0b" },
  { key: "settlementPts", label: "Settlement pts", color: "#38bdf8" },
  { key: "upperPts", label: "Upper pts", color: "#14b8a6", signed: true },
  { key: "downPts", label: "Down pts", color: "#334155" },
];

const AdminDashboardTop = () => {
  const { data: dashRes } = useDashboardQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const { data: matchRes } = useActiveMatchQuery(4, {
    refetchOnMountOrArgChange: true,
  });

  const stats = dashRes?.data || {};
  const fixtures = matchRes?.data || [];

  const fixtureItems = useMemo(() => {
    if (!fixtures.length) {
      return [
        {
          id: "placeholder",
          label: "England v India",
          time: moment().add(2, "hours").format("DD/MM/YYYY HH:mm:ss"),
          icon: "cricket",
        },
      ];
    }
    return fixtures.slice(0, 8).map((m) => ({
      id: m.matchId,
      label: m.matchName,
      time: moment(m.openDate).format("DD/MM/YYYY HH:mm:ss"),
      icon: "cricket",
    }));
  }, [fixtures]);

  const chartMax = useMemo(() => {
    const values = CHART_ITEMS.map((item) => Math.abs(Number(stats[item.key] ?? 0)));
    return Math.max(...values, 1);
  }, [stats]);

  return (
    <section className="adm-dash-top">
      <div className="adm-fixture-bar">
        <div className="adm-fixture-label">Upcoming Fixtures</div>
        <div className="adm-fixture-track">
          <Marquee pauseOnHover gradient={false} speed={35}>
            {fixtureItems.map((fx) => (
              <div className="adm-fixture-item" key={fx.id}>
                <LobbySportIcon name={fx.icon} className="adm-fixture-icon" />
                <span className="adm-fixture-name">{fx.label}</span>
                <span className="adm-fixture-time">{fx.time}</span>
              </div>
            ))}
          </Marquee>
        </div>
      </div>

      <div className="adm-stats-grid">
        {STAT_CARDS.map((card) => (
          <article className={`adm-stat-card adm-stat-card--${card.tone}`} key={card.key}>
            <span className="adm-stat-label">{card.label}</span>
            <strong className="adm-stat-value">{fmt(stats[card.key])}</strong>
          </article>
        ))}
      </div>

      <div className="adm-chart-wrap">
        <div className="adm-chart-legend">
          {CHART_ITEMS.map((item) => (
            <span className="adm-chart-key" key={item.key}>
              <i style={{ background: item.color }} />
              {item.label}
            </span>
          ))}
        </div>
        <div className="adm-chart-panel">
          <div className="adm-chart-y">
            <span>{fmt(chartMax)}</span>
            <span>{fmt(chartMax / 2)}</span>
            <span>0.00</span>
          </div>
          <div className="adm-chart-bars">
            {CHART_ITEMS.map((item) => {
              const raw = Number(stats[item.key] ?? 0);
              const height = Math.max(4, (Math.abs(raw) / chartMax) * 100);
              const negative = raw < 0;
              return (
                <div className="adm-chart-col" key={item.key}>
                  <div className={`adm-chart-bar-slot ${negative ? "is-negative" : ""}`}>
                    <div
                      className="adm-chart-bar"
                      style={{
                        height: `${height}%`,
                        background: item.color,
                      }}
                      title={`${item.label}: ${fmt(raw)}`}
                    />
                  </div>
                  <span className="adm-chart-x">{item.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AdminDashboardTop;
